import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileTextIcon, MousePointerClickIcon } from 'lucide-react';
const pages: {
  src: string;
  label: string;
}[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/aoDJG7u44KCkiRDdR3zf7h/Portaada.png',
  label: 'Case File Cover'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/g61JaWDGFdNUeijcXDVV1i/Captura_de_pantalla_2026-06-22_a_las_12.43.19_a._m..png',
  label: 'Evidence Log'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/jdkqTXgVUMKmtqQr9eng97/Captura_de_pantalla_2026-06-22_a_las_12.43.12_a._m..png',
  label: 'Evidence Notes'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/n2LPs2RKrQr6sgJwsm1zgt/Captura_de_pantalla_2026-06-22_a_las_12.42.39_a._m..png',
  label: 'Interrogation Q&A'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/igYJVjYXE7nDi6CfH3yTnd/Captura_de_pantalla_2026-06-22_a_las_12.42.19_a._m..png',
  label: 'Account Records'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/tzkbY1hDGKD7U2CUFGm9Qn/Captura_de_pantalla_2026-06-22_a_las_12.41.34_a._m..png',
  label: 'Case Narrative'
}];
// Pre-baked "messy" offsets per depth so the stack looks hand-tossed.
const depthOffsets = [{
  x: 0,
  y: 0,
  rotate: -2
}, {
  x: 14,
  y: 10,
  rotate: 3
}, {
  x: -12,
  y: 20,
  rotate: -4
}, {
  x: 10,
  y: 30,
  rotate: 2
}, {
  x: -8,
  y: 40,
  rotate: 5
}];
export function CasePages() {
  // order[0] is the top page; clicking it sends it to the back.
  const [order, setOrder] = useState<number[]>(() => pages.map((_, i) => i));
  const sendTopToBack = () => {
    setOrder(prev => {
      const [first, ...rest] = prev;
      return [...rest, first];
    });
  };
  return <section className="relative bg-[#0a0a0a] py-16 sm:py-24 overflow-hidden" data-id="element-98">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] max-w-[120vw] bg-[#8b1a1a]/10 rounded-full blur-[160px] pointer-events-none" data-id="element-99" />
      <div className="absolute inset-0 bg-noise opacity-10 mix-blend-overlay pointer-events-none" data-id="element-100" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-101">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12 sm:mb-16" data-id="element-102">
          <div className="inline-flex items-center space-x-2 text-[#8b1a1a] mb-3 sm:mb-4 bg-[#8b1a1a]/10 px-4 py-1.5 rounded-full border border-[#8b1a1a]/20 backdrop-blur-sm" data-id="element-103">
            <FileTextIcon className="w-4 h-4" data-id="element-104" />
            <span className="font-typewriter text-xs sm:text-sm tracking-wider uppercase" data-id="element-105">
              Inside the Dossier
            </span>
          </div>
          <h2 className="font-typewriter text-3xl sm:text-5xl text-[#ece3d0] mb-3 drop-shadow-lg" data-id="element-106">
            A Few Pages From The Case File
          </h2>
          <p className="font-serif-body text-[#ece3d0]/70 text-base sm:text-lg max-w-2xl mx-auto" data-id="element-107">
            Real evidence logs, interrogation records and crime-scene notes —
            this is the kind of material that lands on your desk.
          </p>
        </motion.div>

        {/* Stack */}
        <div className="flex flex-col items-center" data-id="element-108">
          <div className="relative w-[260px] h-[360px] sm:w-[360px] sm:h-[500px]" style={{
          perspective: 1200
        }} data-id="element-109">
            <AnimatePresence data-id="element-110">
              {order.map((pageIndex, depth) => {
              const isTop = depth === 0;
              const off = depthOffsets[Math.min(depth, depthOffsets.length - 1)];
              return <motion.div key={pageIndex} layout onClick={isTop ? sendTopToBack : undefined} initial={false} animate={{
                x: off.x,
                y: off.y,
                rotate: off.rotate,
                scale: 1 - depth * 0.03
              }} transition={{
                type: 'spring',
                stiffness: 260,
                damping: 26
              }} whileHover={isTop ? {
                y: off.y - 8,
                rotate: off.rotate * 0.4
              } : undefined} whileTap={isTop ? {
                scale: 0.97
              } : undefined} style={{
                zIndex: pages.length - depth,
                cursor: isTop ? 'pointer' : 'default'
              }} className="absolute inset-0 rounded-md overflow-hidden border border-[#ece3d0]/15 bg-[#15110d] shadow-[0_20px_50px_rgba(0,0,0,0.7)]" data-id="element-111">
                    <img src={pages[pageIndex].src} alt={pages[pageIndex].label} loading="lazy" draggable={false} className="w-full h-full object-cover select-none" data-id="element-112" />
                    {/* Subtle darken on lower pages so the top reads clearly */}
                    {!isTop && <div className="absolute inset-0 bg-[#0a0a0a]/40" data-id="element-113" />}
                    <div className="absolute inset-0 bg-noise mix-blend-overlay opacity-20 pointer-events-none" data-id="element-114" />

                    {isTop && <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center space-x-2 bg-[#0a0a0a]/70 border border-[#ece3d0]/20 backdrop-blur-sm px-3 py-1.5 rounded-full" data-id="element-115">
                        <MousePointerClickIcon className="w-3.5 h-3.5 text-[#8b1a1a]" data-id="element-116" />
                        <span className="font-typewriter text-[10px] sm:text-xs tracking-wider uppercase text-[#ece3d0]/80" data-id="element-117">
                          Tap to reveal the next
                        </span>
                      </div>}
                  </motion.div>;
            })}
            </AnimatePresence>
          </div>

          {/* Page counter dots */}
          <div className="flex items-center justify-center gap-2 mt-10" data-id="element-118">
            {pages.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${order[0] === i ? 'w-6 bg-[#8b1a1a]' : 'w-1.5 bg-[#ece3d0]/25'}`} data-id="element-119" />)}
          </div>
          <p className="font-serif-body text-[#ece3d0]/45 text-sm italic mt-4" data-id="element-120">
            Page {pages.findIndex((_, i) => i === order[0]) + 1} of{' '}
            {pages.length}
          </p>
        </div>
      </div>
    </section>;
}